import type { Asset } from "@/lib/queries";
import { FileType, ImageOff } from "lucide-react";
import { useState } from "react";

const UUID_PREFIX = /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}-/i;

export const assetName = (asset: Asset): string =>
  asset.display_name?.trim() ||
  (asset.r2_key.split("/").pop() ?? asset.r2_key).replace(UUID_PREFIX, "");

export const assetDetails = (asset: Asset): string =>
  asset.width && asset.height
    ? `${asset.width} × ${asset.height}`
    : asset.content_type;

export function AssetPreview({
  asset,
  decorative = false,
  className = "size-full object-contain",
}: {
  asset: Asset;
  decorative?: boolean;
  className?: string;
}): React.JSX.Element {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const failed = failedUrl === asset.url;

  if (!asset.content_type.startsWith("image/")) {
    return (
      <FileType className="size-5 text-muted-foreground" aria-hidden="true" />
    );
  }

  if (failed) {
    return (
      <span
        role={decorative ? undefined : "img"}
        aria-label={
          decorative ? undefined : `Preview unavailable for ${assetName(asset)}`
        }
        className="flex size-full flex-col items-center justify-center gap-1 text-center text-[10px] leading-tight text-muted-foreground"
      >
        <ImageOff className="size-4" aria-hidden="true" />
        {decorative ? null : <span>Preview unavailable</span>}
      </span>
    );
  }

  return (
    <img
      src={asset.url}
      alt={decorative ? "" : assetName(asset)}
      className={className}
      draggable={false}
      onError={() => setFailedUrl(asset.url)}
    />
  );
}
